import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Volume2, VolumeX, Play } from 'lucide-react';

const VIDEO_SRC = '/welcome-week1.mp4';

/**
 * Welcome to Week 1 — coach greeting video.
 * Sits above the "Start Session" card.
 * - Attempts autoplay with sound; iOS/mobile browsers require muted, so falls back to muted + "Tap for Sound"
 * - If autoplay is blocked entirely → shows a giant Play tap target
 */
export default function WelcomeVideoCard() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const attempt = async () => {
      // Try to autoplay with sound first
      try {
        v.muted = false;
        await v.play();
        setMuted(false);
        setPlaying(true);
        return;
      } catch { /* fall through to muted attempt */ }

      // Fallback: autoplay muted (iOS/Safari policy compliant)
      try {
        v.muted = true;
        await v.play();
        setMuted(true);
        setPlaying(true);
      } catch {
        // Total block — user must tap
        setAutoplayBlocked(true);
      }
    };
    const t = setTimeout(attempt, 80);
    return () => clearTimeout(t);
  }, []);

  const handleManualPlay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    setMuted(false);
    v.play().then(() => {
      setPlaying(true);
      setAutoplayBlocked(false);
    }).catch(() => { /* unlikely after gesture */ });
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    setMuted(next);
    if (!next && v.paused) v.play().catch(() => {});
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black border border-[#2563EB]/30 shadow-lg"
      data-testid="week1-welcome-video"
    >
      <div className="absolute top-2 left-2 z-20 flex items-center gap-1.5 bg-[#2563EB] text-white rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-widest shadow">
        <Play size={10} fill="white" /> Welcome — Week 1
      </div>

      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover bg-black"
        src={VIDEO_SRC}
        playsInline
        preload="auto"
        // @ts-ignore — iOS-specific attribute
        webkit-playsinline="true"
        onPlay={() => { setPlaying(true); setAutoplayBlocked(false); }}
        onPause={() => setPlaying(false)}
        data-testid="week1-welcome-video-element"
      />

      {/* Manual play fallback when autoplay blocked */}
      {autoplayBlocked && !playing && (
        <button
          onClick={handleManualPlay}
          className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-black/40 backdrop-blur-[1px]"
          data-testid="week1-welcome-play-btn"
          aria-label="Play welcome video"
        >
          <div className="w-16 h-16 rounded-full bg-[#2563EB] text-white flex items-center justify-center shadow-[0_0_32px_rgba(37,99,235,0.7)] animate-pulse">
            <Play size={28} fill="white" className="ml-1" />
          </div>
          <span className="text-white text-[10px] font-bold uppercase tracking-[0.2em]">Tap to play</span>
        </button>
      )}

      {/* Mute / Unmute toggle */}
      {playing && (
        <button
          onClick={toggleMute}
          className={`absolute top-2 right-2 z-20 flex items-center gap-1.5 rounded-full text-white transition-colors backdrop-blur-sm px-2.5 h-8 text-[10px] font-bold uppercase tracking-widest ${muted ? 'bg-[#2563EB] hover:bg-[#3B82F6] animate-pulse' : 'bg-black/60 hover:bg-black/80'}`}
          data-testid="week1-welcome-mute-btn"
          aria-label={muted ? 'Tap for sound' : 'Mute'}
        >
          {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
          {muted && <span>Sound</span>}
        </button>
      )}
    </motion.div>
  );
}
